import { useRef, useState } from 'react';
import { notify } from '../../../app/Notifications';
import { useRequest } from '../../../hooks';
import {
  getAgentWidgetSettings,
  getFormByName,
  updateAgentWidgetSettings,
  uploadAgentMedia,
} from '../../../api';
import { normalizeAgentWidgetSettingsForForm } from '../utils/normalizeWidgetSettings';
import type {
  AgentWidgetSettings,
  AgentWidgetSettingsRequestBody,
  FormJsonData,
} from '../../../api';

export const useAgentWidgetSettings = () => {
  const [filteredWidgetData, setFilteredWidgetData] =
    useState<AgentWidgetSettings | null>(null);
  const [schema, setSchema] = useState<FormJsonData | null>(null);
  const schemaRef = useRef<FormJsonData | null>(null);

  const resolveSchema = async (): Promise<FormJsonData> => {
    if (schemaRef.current) return schemaRef.current;
    const loaded = (await getFormByName('agentWidgetSettings')) as FormJsonData;
    schemaRef.current = loaded;
    setSchema(loaded);
    return loaded;
  };

  const [getWidgetSettings, initializing] = useRequest(
    (_signal, agentId: string) => {
      return getAgentWidgetSettings(agentId);
    },
    {
      onSuccess: async (data: AgentWidgetSettings) => {
        if (!data) return;

        const resolvedSchema = await resolveSchema();
        const normalized = normalizeAgentWidgetSettingsForForm(
          data,
          resolvedSchema
        );

        setSchema(resolvedSchema);
        setFilteredWidgetData(normalized as AgentWidgetSettings);
      },
      onError: () => {
        notify.error('Failed to retrieve widget settings');
      },
    }
  );

  const [handleUpdateWidgetSettings, updating] = useRequest(
    (_signal, agentId: string, data: AgentWidgetSettingsRequestBody) => {
      return updateAgentWidgetSettings(agentId, data);
    },
    {
      onSuccess: async (data: AgentWidgetSettings) => {
        if (!data) return;

        const resolvedSchema = await resolveSchema();
        const normalized = normalizeAgentWidgetSettingsForForm(
          data,
          resolvedSchema
        );

        setSchema(resolvedSchema);
        setFilteredWidgetData(normalized as AgentWidgetSettings);
        notify.success('Widget settings updated successfully');
      },
      onError: () => {
        notify.error('Failed to update widget settings');
      },
    }
  );

  const [importWidgetSettingsFromAgent, importing] = useRequest(
    (_signal, sourceAgentId: string) => {
      return getAgentWidgetSettings(sourceAgentId);
    },
    {
      onSuccess: async (data: AgentWidgetSettings) => {
        if (!data) return;

        const resolvedSchema = await resolveSchema();
        const normalized = normalizeAgentWidgetSettingsForForm(
          data,
          resolvedSchema
        );

        setFilteredWidgetData(normalized as AgentWidgetSettings);
      },
      onError: () => {
        notify.error('Failed to load widget settings from the selected agent');
      },
    }
  );

  const [handleUploadMedia, uploading] = useRequest(
    (_signal, agentId: string, file: Blob) => {
      return uploadAgentMedia({ file }, agentId);
    },
    {
      onSuccess: () => {
        notify.success('Media uploaded successfully');
      },
      onError: () => {
        notify.error('Failed to upload media');
      },
    }
  );

  return {
    data: filteredWidgetData,
    isLoading: initializing || updating,
    importing,
    schema,
    handleUpdateWidgetSettings,
    getWidgetSettings,
    importWidgetSettingsFromAgent,
    setWidgetData: setFilteredWidgetData,
    handleUploadMedia,
    uploading,
  };
};
